import React from "react";
import { motion } from "framer-motion";
import { ArrowDown, MessageCircle } from "lucide-react";
import { Button } from "./ui/button";
import { LOGO_URL } from "../data/mock";

export const HeroSection = () => {
  const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-[#f0faf7]">
      {/* Background image — Dubai street with wheelie bin */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage:
            "url('https://customer-assets.emergentagent.com/job_3d163003-53b2-4a24-b636-2fbdde0d9a2c/artifacts/69la9fsx_IMG_0310.jpg')",
        }}
        aria-hidden="true"
      />
      {/* Readability overlay — stronger on the left where text lives */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(to right, rgba(240,250,247,0.96) 0%, rgba(240,250,247,0.85) 35%, rgba(240,250,247,0.55) 60%, rgba(240,250,247,0.25) 85%, rgba(240,250,247,0.1) 100%)",
        }}
        aria-hidden="true"
      />
      {/* Mobile: vertical gradient overlay — much lighter at top/bottom so the
          Burj Khalifa, palm and bin show through clearly, denser only in the
          middle band where the text sits so it stays readable. */}
      <div
        className="absolute inset-0 lg:hidden"
        style={{
          background:
            "linear-gradient(to bottom, rgba(240,250,247,0.05) 0%, rgba(240,250,247,0.20) 12%, rgba(240,250,247,0.70) 28%, rgba(240,250,247,0.70) 72%, rgba(240,250,247,0.20) 88%, rgba(240,250,247,0.05) 100%)",
        }}
        aria-hidden="true"
      />

      {/* Decorative Elements */}
      <div className="absolute top-20 right-10 w-72 h-72 bg-[#16C79A]/8 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-10 w-96 h-96 bg-[#16C79A]/6 rounded-full blur-3xl" />
      <div className="absolute top-1/3 left-1/4 w-48 h-48 bg-[#16C79A]/5 rounded-full blur-2xl" />

      {/* Subtle grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage:
            "radial-gradient(circle, #0C2D48 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-28 pb-20 w-full">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
          >
            {/* Mobile-only prominent logo (desktop has it in the right column) */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="lg:hidden flex justify-center mb-6"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_3d163003-53b2-4a24-b636-2fbdde0d9a2c/artifacts/w1lpcwp5_Steamers%20Logo%20%28transparent%29.png"
                alt="Steamers - Professional Bin Cleaning in Dubai"
                className="h-24 sm:h-28 w-auto object-contain drop-shadow-md"
              />
            </motion.div>

            <div className="inline-flex items-center gap-2 bg-[#16C79A]/10 border border-[#16C79A]/20 rounded-full px-4 py-1.5 mb-8">
              <span className="w-2 h-2 rounded-full bg-[#16C79A] animate-pulse" />
              <span className="text-[#0C2D48] text-sm font-medium">
                Professional Wheelie Bin Cleaning Across Dubai
              </span>
            </div>

            <h1 className="text-5xl lg:text-6xl xl:text-7xl font-extrabold text-[#0C2D48] leading-[1.08] tracking-tight mb-4">
              Your External Bins,{" "}
              <span className="relative">
                <span className="relative z-10 text-[#16C79A]">Spotless</span>
                <span className="absolute bottom-2 left-0 right-0 h-3 bg-[#16C79A]/20 rounded-full -z-0" />
              </span>
              <br />
              & Sanitised
            </h1>

            <p className="text-base text-slate-500 leading-relaxed max-w-lg mb-6 italic">
              Even though our external bins are lined, they are still an invisible
              breeding ground for bacteria and pests (fly larvae, mosquitoes,
              rodents).
            </p>

            <p className="text-lg text-slate-600 leading-relaxed max-w-lg mb-4">
              Steamers use high-pressure cleaning rigs and hospital grade
              disinfectants to deep clean, sanitise, and deodorise your wheelie
              bins. Say goodbye to bacteria, bad odours, and pests for good.
            </p>
            <p className="text-lg text-slate-600 leading-relaxed max-w-lg mb-10">
              Sign up below or chat to
              us on WhatsApp. We're real people from the UK and we'd love to
              hear from you!
            </p>

            <div className="flex flex-wrap gap-4 mb-12">
              <Button
                onClick={() => scrollTo("#signup")}
                className="bg-[#16C79A] hover:bg-[#0C2D48] text-white font-bold px-8 py-6 rounded-full text-lg transition-all duration-300 hover:shadow-xl hover:shadow-[#16C79A]/20 hover:-translate-y-0.5"
              >
                Sign Up Today
              </Button>
              <Button
                asChild
                className="bg-[#25D366] hover:bg-[#1da851] text-white font-bold px-8 py-6 rounded-full text-lg transition-all duration-300 hover:shadow-xl hover:shadow-[#25D366]/20 hover:-translate-y-0.5"
              >
                <a
                  href="https://wa.me/971502856909"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <MessageCircle size={20} className="mr-2" />
                  WhatsApp Us
                </a>
              </Button>
              <Button
                onClick={() => scrollTo("#reasons")}
                variant="outline"
                className="border-2 border-[#0C2D48]/20 text-[#0C2D48] font-semibold px-8 py-6 rounded-full text-lg hover:bg-[#0C2D48]/5 transition-all duration-300 hover:-translate-y-0.5"
              >
                Learn More
              </Button>
            </div>

          </motion.div>

          {/* Right - Logo Display */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
            className="hidden lg:flex items-center justify-center"
          >
            <div className="relative">
              {/* Glow ring */}
              <div className="absolute inset-0 bg-[#16C79A]/10 rounded-full blur-3xl scale-110 animate-pulse" />
              <div className="relative bg-white/60 backdrop-blur-xl rounded-3xl p-8 shadow-2xl shadow-[#16C79A]/10 border border-white/60">
                <img
                  src={LOGO_URL}
                  alt="Steamers - Professional Bin Cleaning"
                  className="w-[400px] h-auto object-contain"
                />
                {/* Floating badges */}
                <motion.div
                  animate={{ y: [-6, 6, -6] }}
                  transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute -top-4 -right-4 bg-white rounded-2xl shadow-lg shadow-[#16C79A]/10 px-4 py-2 border border-[#e0f5ef]"
                >
                  <span className="text-sm font-bold text-[#0C2D48]">Trusted Local Service</span>
                </motion.div>
                <motion.div
                  animate={{ y: [6, -6, 6] }}
                  transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute -bottom-4 -left-4 bg-white rounded-2xl shadow-lg shadow-[#16C79A]/10 px-4 py-2 border border-[#e0f5ef]"
                >
                  <span className="text-sm font-bold text-[#16C79A]">100% Eco-Friendly</span>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <button
            onClick={() => scrollTo("#reasons")}
            className="flex flex-col items-center gap-2 text-[#0C2D48]/40 hover:text-[#16C79A] transition-colors"
          >
            <span className="text-xs font-medium uppercase tracking-wider">
              Discover Why
            </span>
            <ArrowDown size={20} />
          </button>
        </motion.div>
      </div>
    </section>
  );
};
